import { createContext, useContext } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTokenProvider } from '@/contexts/TokenProviderContext';

/**
 * Identity state returned by useIdentity
 */
export interface IdentityState {
  signin: () => Promise<any>;
  signinRedirect: () => Promise<void>;
  signout: () => Promise<void>;
  loading: boolean;
  error: any;
  isAuthenticated: boolean;
  user: any;
  getAccessToken: () => Promise<string>;
}

/**
 * Context for overriding identity in modes without AuthProvider (DevAuth).
 * When set, useIdentity returns this value instead of calling useAuth().
 */
export const IdentityOverrideContext = createContext<IdentityState | null>(null);

/**
 * Custom hook for Access Identity authentication
 *
 * Supports four deployment modes:
 * - Native Tab: Token retrieval delegated to parent Access Workspace frame
 * - Standalone: Token retrieval via react-oidc-context with error handling
 * - E2B: Same as Standalone
 * - Dev Auth: No OIDC — uses IdentityOverrideContext
 *
 * Components don't need to know which mode is active.
 */
export function useIdentity(): IdentityState {
  const override = useContext(IdentityOverrideContext);
  if (override) {
    return override;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const auth = useAuth();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { getAccessToken } = useTokenProvider();

  return {
    signin: auth.signinSilent,
    signinRedirect: auth.signinRedirect,
    signout: auth.signoutRedirect,
    loading: auth.isLoading,
    error: auth.error,
    isAuthenticated: auth.isAuthenticated,
    user: auth.user,
    getAccessToken,
  };
}
